import { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from './LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Cache globale per le traduzioni (persiste tra i render)
const translationCache = new Map();

export const useTranslation = () => {
  const { language } = useLanguage();
  const [isTranslating, setIsTranslating] = useState(false);
  const pendingRequests = useRef(new Map());

  // Genera una chiave di cache
  const getCacheKey = useCallback((text, targetLang) => {
    return `${targetLang}:${text}`;
  }, []);

  // Traduce un singolo testo
  const translateText = useCallback(async (text) => {
    if (!text || language === 'it') return text;
    
    const cacheKey = getCacheKey(text, language);
    
    // Controlla la cache
    if (translationCache.has(cacheKey)) {
      return translationCache.get(cacheKey);
    }
    
    // Controlla se c'è già una richiesta pendente per questo testo
    if (pendingRequests.current.has(cacheKey)) {
      return pendingRequests.current.get(cacheKey);
    }
    
    // Crea una nuova richiesta
    const requestPromise = (async () => {
      try {
        const response = await axios.post(`${BACKEND_URL}/api/translate`, {
          texts: [text],
          target_language: language
        });
        
        const translation = response.data.translations?.[0] || text;
        translationCache.set(cacheKey, translation);
        return translation;
      } catch (error) {
        console.error('Translation error:', error);
        return text;
      } finally {
        pendingRequests.current.delete(cacheKey);
      }
    })();
    
    pendingRequests.current.set(cacheKey, requestPromise);
    return requestPromise;
  }, [language, getCacheKey]);

  // Traduce più testi in batch (più efficiente)
  const translateBatch = useCallback(async (texts) => {
    if (!texts || texts.length === 0 || language === 'it') {
      return texts;
    }
    
    setIsTranslating(true);
    
    try {
      // Filtra i testi che non sono in cache
      const textsToTranslate = [];
      const cachedResults = new Map();
      
      texts.forEach((text, index) => {
        const cacheKey = getCacheKey(text, language);
        if (translationCache.has(cacheKey)) {
          cachedResults.set(index, translationCache.get(cacheKey));
        } else {
          textsToTranslate.push({ text, index });
        }
      });
      
      // Se tutto è in cache, ritorna subito
      if (textsToTranslate.length === 0) {
        return texts.map((_, index) => cachedResults.get(index));
      }
      
      // Chiama l'API per i testi non in cache
      const response = await axios.post(`${BACKEND_URL}/api/translate`, {
        texts: textsToTranslate.map(t => t.text),
        target_language: language
      });
      
      const translations = response.data.translations || [];
      
      // Aggiorna la cache e costruisci il risultato
      textsToTranslate.forEach((item, i) => {
        const translation = translations[i] || item.text;
        const cacheKey = getCacheKey(item.text, language);
        translationCache.set(cacheKey, translation);
        cachedResults.set(item.index, translation);
      });
      
      return texts.map((_, index) => cachedResults.get(index) || texts[index]);
      
    } catch (error) {
      console.error('Batch translation error:', error);
      return texts;
    } finally {
      setIsTranslating(false);
    }
  }, [language, getCacheKey]);

  // Pulisce la cache per una lingua specifica
  const clearCache = useCallback((targetLang) => {
    const prefix = `${targetLang}:`;
    for (const key of translationCache.keys()) {
      if (key.startsWith(prefix)) {
        translationCache.delete(key);
      }
    }
  }, []);

  return {
    translateText,
    translateBatch,
    clearCache,
    isTranslating,
    currentLanguage: language
  };
};

export default useTranslation;
